import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import axiosInstance from '../utils/axiosConfig';
import { getApiError, getBlobApiError } from '../utils/apiError';
import { Modal } from './common';

function EmployeesWithoutEquipmentModal({ onClose, onAssign, asset }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [useManual, setUseManual] = useState(false);
  const [manualText, setManualText] = useState('');
  const [showConstanciaModal, setShowConstanciaModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchEmployeesWithoutEquipment = async () => {
      try {
        const response = await axiosInstance.get('/api/empleados/sin-equipo');
        setEmployees(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching employees:', error);
        setError('Error al cargar los empleados');
        setLoading(false);
      }
    };

    fetchEmployeesWithoutEquipment();
  }, []);

  if (!asset || !asset.id) {
    console.error('Asset prop is required with an id');
    return null;
  }

  const handleAssign = async () => {
    if (!asset.id || submitting) return;
    if (!useManual && !selectedEmployee) {
      setError('Seleccione un empleado o active Asignación Manual');
      return;
    }
    if (useManual && !manualText.trim()) {
      setError('Ingrese un texto para Asignación Manual');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const payload = useManual
        ? { asignacion_manual: manualText.trim() }
        : { empleado_id: selectedEmployee.id };

      await axiosInstance.post(`/api/activos/${asset.id}/asignar`, payload);
      setShowConstanciaModal(true);
    } catch (error) {
      console.error('Error:', error);
      setError(getApiError(error, 'Error al asignar el equipo'));
    } finally {
      setSubmitting(false);
    }
  };

  // En este punto el equipo ya quedó asignado: cualquier forma de cerrar
  // (botones, la X o Escape) tiene que refrescar la tabla. Si no, el equipo
  // sigue apareciendo libre y se le puede asignar otra vez encima.
  const finishAssignment = () => {
    onAssign(selectedEmployee || { nombre: manualText });
    onClose();
  };

  const handleConstanciaDecision = async (decision) => {
    if (submitting) return;
    setSubmitting(true);

    if (decision) {
      try {
        const response = await axiosInstance.get(`/api/activos/${asset.id}/constancia`, {
          responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `constancia_entrega_${selectedEmployee?.nombre || 'asignacion_manual'}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error('Error generando constancia:', error);
        toast.error(await getBlobApiError(error, 'El equipo quedó asignado, pero no se pudo generar la constancia'));
      }
    }

    finishAssignment();
  };

  if (showConstanciaModal) {
    return (
      <Modal isOpen={true} onClose={finishAssignment} size="md">
        <Modal.Header onClose={finishAssignment}>Confirmar Asignación</Modal.Header>
        <Modal.Body>
          <p>{useManual ? 'Asignación manual completada.' : '¿Desea generar la constancia de entrega?'}</p>
          <div className="asset-info">
            <p><strong>Equipo:</strong> {asset.nombre_equipo}</p>
            <p><strong>Serial:</strong> {asset.serial}</p>
            <p><strong>{useManual ? 'Asignado a' : 'Empleado'}:</strong> {useManual ? manualText : (selectedEmployee?.nombre || '')}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button onClick={() => handleConstanciaDecision(false)} className="btn btn-secondary" disabled={submitting}>
            No Generar
          </button>
          <button onClick={() => handleConstanciaDecision(true)} className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Generando...' : 'Generar PDF'}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal isOpen={!!asset} onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>Asignar {asset.tipo} - {asset.nombre_equipo}</Modal.Header>
      <Modal.Body>
        <div className="asset-info">
          <p><strong>Tipo:</strong> {asset.tipo}</p>
          <p><strong>Equipo:</strong> {asset.nombre_equipo}</p>
          <p><strong>Modelo:</strong> {asset.modelo}</p>
          <p><strong>Serial:</strong> {asset.serial}</p>
          <p><strong>RAM:</strong> {asset.ram}</p>
          <p><strong>Disco:</strong> {asset.disco}</p>
        </div>

        {error && <p className="error-message">{error}</p>}

        {loading ? (
          <p>Cargando empleados...</p>
        ) : (
          <div className="form-group">
            <div className="manual-assignment-row">
              <label>
                <input
                  type="checkbox"
                  checked={useManual}
                  onChange={(e) => {
                    setUseManual(e.target.checked);
                    setError(null);
                  }}
                />{' '}
                Asignación Manual
              </label>
            </div>
            {!useManual && (
              <>
                <label htmlFor="empleado">Seleccionar Empleado:</label>
                <select
                  id="empleado"
                  value={selectedEmployee?.id || ''}
                  onChange={(e) => {
                    const employee = employees.find(emp => emp.id === parseInt(e.target.value));
                    setSelectedEmployee(employee);
                  }}
                  className="form-control"
                >
                  <option value="">Seleccione un empleado</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.nombre} - {employee.departamento}
                    </option>
                  ))}
                </select>
              </>
            )}
            {useManual && (
              <>
                <label htmlFor="asignacionManual">Texto de Asignación Manual:</label>
                <input
                  id="asignacionManual"
                  type="text"
                  value={manualText}
                  onChange={(e) => setManualText(e.target.value)}
                  className="form-control"
                  placeholder="Ej: Departamento de Contabilidad, Juan Pérez (asesor)"
                />
              </>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button onClick={onClose} className="btn btn-secondary" disabled={submitting}>
          Cancelar
        </button>
        <button
          onClick={handleAssign}
          className="btn btn-primary"
          disabled={loading || submitting || (!useManual && !selectedEmployee) || (useManual && !manualText.trim())}
        >
          {submitting ? 'Asignando...' : 'Asignar'}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

EmployeesWithoutEquipmentModal.propTypes = {
  asset: PropTypes.shape({
    id: PropTypes.number.isRequired,
    tipo: PropTypes.string.isRequired,
    nombre_equipo: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onAssign: PropTypes.func.isRequired,
};

export default EmployeesWithoutEquipmentModal;